import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Pencil, X, FileSpreadsheet } from "lucide-react";
import { Modal, Field } from "@/components/ui-mm/Modal";
import { useData } from "@/context/DataContext";
import { useConfirm } from "@/context/ConfirmContext";
import { useStats } from "@/hooks/useStats";
import { ds, money, getCurrency, periodLabel } from "@/lib/money";
import { t } from "@/lib/i18n";

export function useTick(ms = 15000) {
  const [, setT] = useState(0);
  useEffect(() => { const i = setInterval(() => setT((x) => x + 1), ms); return () => clearInterval(i); }, [ms]);
}

export function EditDialog({ item, kind, onClose }) {
  const { update } = useData();
  const [a, setA] = useState("");
  const [n, setN] = useState("");
  useEffect(() => { if (item) { setA(String(item.amt)); setN(item.note || ""); } }, [item]);
  const save = async () => {
    const v = parseFloat(a);
    if (!(v > 0)) return;
    const { id, created_at, legacy_id, ...rest } = item;
    try { await update(kind, item.id, { ...rest, amt: Math.round(v * 100) / 100, note: n.trim() }); onClose(); } catch { /* Keep the edit dialog open after an error. */ }
  };
  return (
    <Modal open={!!item} onClose={onClose} title={t("Edit entry")} testId="edit-entry-dialog">
      <div className="space-y-3">
        <Field label={t("Amount")}><input className="mm-input num" type="number" step="0.01" value={a} onChange={(e) => setA(e.target.value)} data-testid="edit-entry-amount-input" /></Field>
        <Field label={t("Note")}><input className="mm-input" value={n} maxLength={200} onChange={(e) => setN(e.target.value)} data-testid="edit-entry-note-input" /></Field>
      </div>
      <div className="mt-6 flex justify-end gap-2"><button className="mm-btn ghost" onClick={onClose}>{t("Cancel")}</button><button className="mm-btn" onClick={save} data-testid="edit-entry-save-button">{t("Save")}</button></div>
    </Modal>
  );
}

const cell = (v) => { const s = String(v ?? ""); return /[",\n\r]/.test(s) || /^[=+\-@]/.test(s) ? `"${(/^[=+\-@]/.test(s) ? "'" : "") + s.replace(/"/g, '""')}"` : s; };

function exportCsv(rows, catInfo, label) {
  const cur = getCurrency();
  const lines = [["Date", "Category", "Type", "Note", `Amount (${cur})`, "Recurring"].join(",")];
  [...rows].sort((a, b) => a.date.localeCompare(b.date) || a.created_at - b.created_at).forEach((i) => {
    const c = catInfo(i.cat);
    lines.push([i.date, c.n, c.u ? "Unnecessary" : "Necessary", i.note, i.amt.toFixed(2), i.recurring_id ? "Yes" : ""].map(cell).join(","));
  });
  lines.push(["", "", "", "Total", rows.reduce((t2, i) => t2 + i.amt, 0).toFixed(2), ""].map(cell).join(","));
  const url = URL.createObjectURL(new Blob(["\ufeff" + lines.join("\r\n")], { type: "text/csv;charset=utf-8" }));
  const a = Object.assign(document.createElement("a"), { href: url, download: `budgetpulse-history-${label.replace(/[^\w-]+/g, "-")}.csv` });
  a.click();
  URL.revokeObjectURL(url);
}

function Item({ i, win, onEdit }) {
  const { catInfo, remove } = useData();
  const confirm = useConfirm();
  const c = catInfo(i.cat);
  const left = win - (Date.now() - i.created_at);
  const del = async () => { if (await confirm(`${t("Delete")} ${money(i.amt)} · ${c.n}?`, { danger: true, yes: t("Delete") })) remove("expenses", i.id).catch(() => {}); };
  return (
    <motion.div layout initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }} className="group flex items-center gap-3 rounded-[var(--ri)] px-2 py-2.5 transition-colors hover:bg-[var(--bg)]" data-testid={`history-item-${i.id}`}>
      <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full text-lg" style={{ background: `color-mix(in srgb, ${c.c} 18%, transparent)` }}>
        {c.e}
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-semibold">{c.n}{i.recurring_id ? " · 🔁" : ""}</div>
        <div className="truncate text-xs text-[var(--mu)]">{i.note || "—"}{left > 0 ? ` · ${t("editable")} ${Math.ceil(left / 60000)}m` : ""}</div>
      </div>
      <div className="num text-sm font-bold">{money(i.amt)}</div>
      {left > 0 ? (
        <div className="flex">
          <button className="grid h-9 w-9 place-items-center rounded-full hover:bg-[var(--card)]" onClick={() => onEdit(i)} aria-label="Edit" data-testid={`history-edit-${i.id}`}><Pencil className="h-3.5 w-3.5" /></button>
          <button className="grid h-9 w-9 place-items-center rounded-full text-[var(--dg)] hover:bg-[var(--card)]" onClick={del} aria-label="Delete" data-testid={`history-delete-${i.id}`}><X className="h-4 w-4" /></button>
        </div>
      ) : null}
    </motion.div>
  );
}

export default function History() {
  const { db, catInfo, mode } = useData();
  const st = useStats();
  const [q, setQ] = useState("");
  const [search, setSearch] = useState("");
  const [allDates, setAllDates] = useState(false);
  const [edit, setEdit] = useState(null);
  useTick();
  const win = db.settings.prefs.editMinutes * 60000;
  const source = allDates ? db.expenses : st.m;
  const used = [...new Set(source.map((i) => i.cat))];
  const needle = search.trim().toLocaleLowerCase();
  const hm = source.filter((i) => (!q || i.cat === q) &&
    (!needle || [i.note, i.date, catInfo(i.cat).n, String(i.amt)].some((v) => String(v || "").toLocaleLowerCase().includes(needle))));
  const days = {};
  hm.forEach((i) => (days[i.date] = days[i.date] || []).push(i));
  const label = (allDates ? "all-dates" : periodLabel(mode, st.s, st.e)) + (q ? `-${catInfo(q).n}` : "");
  return (
    <section className="mm-card mm-rise" id="history" data-testid="history-timeline-grouped-list">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="mm-h2 text-lg">{t("History")}</h2>
        <button className="mm-btn ghost sm" disabled={!hm.length} onClick={() => exportCsv(hm, catInfo, label)} data-testid="history-export-spreadsheet-button"><FileSpreadsheet className="h-4 w-4" /> {t("Export to spreadsheet")}</button>
      </div>
      <label className="mb-3 block"><span className="sr-only">Search transactions</span><input type="search" className="mm-input" placeholder="Search history by note, date, category or amount" value={search} onChange={(e) => setSearch(e.target.value)} aria-label="Search transactions" /></label>
      <label className="mb-3 flex cursor-pointer items-center gap-2 text-xs font-medium text-[var(--mu)]"><input type="checkbox" checked={allDates} onChange={(e) => setAllDates(e.target.checked)} /> Search and export all transaction dates (not only the selected period)</label>
      <div className="no-scrollbar -mx-1 mb-3 flex gap-2 overflow-x-auto px-1 pb-1">
        <button className={`mm-chip ${q ? "" : "on"}`} onClick={() => setQ("")} data-testid="history-filter-all">🗂 {t("All")}</button>
        {used.map((id) => { const c = catInfo(id); return <button key={id} className={`mm-chip ${q === id ? "on" : ""}`} onClick={() => setQ(id)} data-testid={`history-filter-${id.replace(/\s+/g, "-").toLowerCase()}`}>{c.e} {c.n}</button>; })}
      </div>
      <div className="mb-3 text-sm text-[var(--mu)]" data-testid="history-filter-total">{hm.length} {t("results")} · <b className="num text-[var(--tx)]">{money(hm.reduce((a, i) => a + i.amt, 0))}</b></div>
      <div className="bp-scroll max-h-[min(62vh,560px)] overflow-y-auto overscroll-contain rounded-[var(--ri)] border border-[var(--bd)] bg-[color-mix(in_srgb,var(--bg)_50%,var(--card))] p-2 pr-1" data-testid="history-scroll-box">
        {!hm.length && <p className="p-4 text-sm text-[var(--mu)]" data-testid="history-empty">{t("No expenses in this period.")}</p>}
        <div className="space-y-4">
          {Object.keys(days).sort().reverse().map((d) => (
            <div key={d}>
              <div className="sticky top-0 z-10 mb-1 flex justify-between rounded-md bg-[var(--card)] px-2 py-1.5 text-xs font-semibold uppercase tracking-wider text-[var(--mu)]">
                <span>{ds(new Date(d + "T00:00"), { weekday: "long", day: "numeric", month: "short" })}</span>
                <span className="num">{money(days[d].reduce((a, i) => a + i.amt, 0))}</span>
              </div>
              <AnimatePresence initial={false}>{[...days[d]].sort((a, b) => b.created_at - a.created_at).map((i) => <Item key={i.id} i={i} win={win} onEdit={setEdit} />)}</AnimatePresence>
            </div>
          ))}
        </div>
      </div>
      <EditDialog item={edit} kind="expenses" onClose={() => setEdit(null)} />
    </section>
  );
}
